import { useOne, useTranslation } from "@refinedev/core";
import { PencilIcon } from "@heroicons/react/solid";
import { Link, useParams } from "react-router-dom";
import Button from "components/button";
import { Breadcrumbs } from "components/breadcrumbs";
import { FullScreenLoading } from "components/fullScreenLoading";
import { HistoryLogButton } from "components/historyLogButton";
import { isHaveActionPermission } from "utills/helpers";
import { getConfig } from "../config";
import { PickupEventForm } from "../form";
import { getEventLabels } from "../labels";
import { PickupEventDetail, PickupEventVariant } from "../types";

export const PickupEventDetailPage = ({ variant }: { variant: PickupEventVariant }) => {
  const config = getConfig(variant);
  const { translate, getLocale } = useTranslation();
  const language = getLocale();
  const labels = getEventLabels(variant, translate);
  const { id } = useParams();

  const {
    data,
    isFetching,
    isPreviousData,
    isLoading,
  } = useOne<PickupEventDetail>({
    resource: config.detailResource,
    id,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!id,
    } as any,
  });

  const eventDetail = data?.data ?? null;

  if (isLoading) return <FullScreenLoading />;

  return (
    <div className={variant === "wps" ? "w-full" : "wrapper"}>
      <Breadcrumbs
        type={variant === "wps" ? "wps" : undefined}
        customPageLabel={variant === "admin" ? labels.detailTitle : undefined}
        breadcrumbItems={
          variant === "wps"
            ? [
                { label: labels.checkIn },
                { label: labels.pageTitle, href: config.basePath },
                { label: labels.detailTitle, href: `${config.basePath}/:id/detail` },
              ]
            : undefined
        }
        disableBorder={variant === "wps" ? true : undefined}
        additionalLabel={
          <div className="flex items-center gap-x-2.5">
            <HistoryLogButton
              resource={config.historyResource(id ?? "")}
              className="w-11 h-11"
              lang={variant === "wps" ? language : undefined}
            />
            {isHaveActionPermission(config.permissionResource, config.permissionApp, "edit") ? (
              <Link to={config.editPath(id ?? "")}>
                <Button className="h-11 w-11 sm:h-auto sm:w-auto">
                  <span className="sr-only">{labels.edit}</span>
                  <PencilIcon className="w-4 h-4 text-white" />{" "}
                  <span className="hidden sm:block sm:ml-2">{labels.edit}</span>
                </Button>
              </Link>
            ) : null}
          </div>
        }
      />

      {eventDetail ? (
        <PickupEventForm
          backPath={config.detailPath(id ?? "")}
          defaultData={eventDetail}
          onSubmit={() => {}}
          readOnly
          variant={variant}
          key={eventDetail.id}
        />
      ) : null}

      {isFetching && isPreviousData ? <FullScreenLoading /> : null}
    </div>
  );
};
